/*
  Opens and scrolls to the "Replacements & Add Ons" (or any other) description tab from a URL
  hash, e.g. product.url + "#replacements-add-ons" (used by snippets/list-pdp-card.liquid's
  "View Replacements & Add Ons" link).

  This used to live inside assets/page-product.js, but that file:
  1. was never actually loaded on the product template, and
  2. crashed on its very first line whenever the header's "Sticky" setting is off
     (document.querySelector("sticky-header") is null in that case),
  which silently killed this logic along with everything else in that file.

  Every <details class="tab-content--body" open> in this template renders already-open by
  default (snippets/snippet-description.liquid hardcodes the `open` attribute), and
  section-product-description.css has no rule that hides an inactive panel - so on this
  template all tabs' content is simultaneously visible, stacked one after another, and the
  "active" class on the nav heading is purely cosmetic. That means opening the target tab
  is not enough: every *other* panel has to be explicitly closed too, or its (also "open")
  content keeps showing above the one we actually want visible.

  This script is intentionally self-contained (no dependency on page-product.js, on
  product-description.js's custom element having already upgraded, or on any function/variable
  declared elsewhere) so it can't be broken by anything else on the page failing.
*/
(function () {
  function getStickyOffset() {
    var stickyHeader = document.querySelector('sticky-header');
    return stickyHeader ? stickyHeader.offsetHeight : 0;
  }

  // Gap kept between the bottom of the sticky header and the top of the section - same fixed
  // value on both mobile and desktop.
  var SCROLL_MARGIN = 40;

  // Extra distance added on top of the computed target scroll position.
  var EXTRA_SCROLL = 80;

  // The product gallery is position:sticky within .shopify-section-main-product, so it stays
  // pinned to the top of the viewport until that whole section's bottom has scrolled past -
  // which can still be below the description section's own top. Landing exactly at the top of
  // the description section isn't enough in that case; the gallery is still visibly "stuck"
  // above it. Target whichever point requires scrolling further: the description section's
  // top, or past the end of the (possibly still-sticky) main product section.
  function computeScrollTarget(el) {
    var offset = getStickyOffset() + SCROLL_MARGIN;
    var targets = [];

    if (el) {
      targets.push(el.getBoundingClientRect().top + window.pageYOffset - offset);
    }

    var mainProductSection = document.querySelector('.shopify-section-main-product');
    if (mainProductSection) {
      targets.push(mainProductSection.getBoundingClientRect().bottom + window.pageYOffset - offset);
    }

    return targets.length ? Math.max.apply(null, targets) + EXTRA_SCROLL : null;
  }

  function scrollToElement(el) {
    var target = computeScrollTarget(el);
    if (target === null) return;
    window.scrollTo({ top: target, behavior: 'smooth' });
  }

  // Images, review widgets etc. inside the main product section can keep resizing for a bit
  // even after the page "looked" settled enough to start the scroll above - shifting the
  // target out from under a scroll that's already in flight. Re-measure the target a few
  // times over the next ~2s and nudge back into place if it moved, instead of trusting the one
  // calculation made before the scroll started. Stops early once the target holds steady.
  //
  // On mobile, <accordion-component data-close-screen="(max-width: 767px)"> re-closes its
  // <details> on its own (responsive open/close), which can undo the `open` we set on the
  // target panel shortly after we set it. reactivate, if given, re-asserts the open/active
  // state on every check too, so it keeps winning until the accordion's own init settles down.
  function correctScrollWhileSettling(el, reactivate) {
    var checksLeft = 8;
    var intervalMs = 250;
    var lastTarget = null;
    var stableChecks = 0;

    function check() {
      if (reactivate) reactivate();

      var target = computeScrollTarget(el);
      if (target !== null) {
        stableChecks = (lastTarget !== null && Math.abs(target - lastTarget) < 2) ? stableChecks + 1 : 0;
        lastTarget = target;

        if (Math.abs(window.pageYOffset - target) > 4) {
          window.scrollTo({ top: target, behavior: 'smooth' });
        }
      }

      checksLeft--;
      if (checksLeft > 0 && stableChecks < 3) {
        setTimeout(check, intervalMs);
      }
    }

    setTimeout(check, intervalMs);
  }

  // Waits until the page's total height stops changing (e.g. the replacements carousel
  // finishing its slide-width/clone setup, images loading) before calling back, instead of a
  // blind fixed delay - that was causing a visible scroll-then-correct jump: we'd scroll to a
  // position computed before the slider settled, then the page would resize under us and we'd
  // have to additionally sqeeze everything into place. Capped so it can never hang.
  function whenLayoutSettles(callback, maxWaitMs) {
    var deadline = Date.now() + (maxWaitMs || 600);
    var lastHeight = document.documentElement.scrollHeight;
    var stableFrames = 0;

    function check() {
      var height = document.documentElement.scrollHeight;
      if (height === lastHeight) {
        stableFrames++;
      } else {
        stableFrames = 0;
        lastHeight = height;
      }
      if (stableFrames >= 10 || Date.now() >= deadline) {
        callback();
      } else {
        requestAnimationFrame(check);
      }
    }
    requestAnimationFrame(check);
  }

  function activateTab(root, heading, body) {
    // Close every panel first (only this one has to end up open), then open the target.
    root.querySelectorAll('.tab-content--heading').forEach(function (item) {
      item.classList.remove('active');
    });
    root.querySelectorAll('.tab-content--body').forEach(function (item) {
      item.classList.remove('active');
      item.removeAttribute('open');
    });

    if (heading) heading.classList.add('active');
    if (body) {
      body.classList.add('active');
      body.setAttribute('open', '');
    }

    // Deliberately NOT calling heading.click() here: the heading is a real <a href="#tab_id">,
    // and product-description.js's click delegation doesn't reliably preventDefault() it, so
    // the browser was performing its own instant native jump-to-element scroll (updating the
    // URL hash to #tab_id in the process) racing against - and winning over - the controlled
    // smooth scroll below. All the state that click would have set (the "active" classes) is
    // already set above, so nothing is lost by skipping it.
  }

  function run() {
    try {
      var hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      if (!hash) return;

      var root = document.querySelector('product-description-component');
      if (!root) return;

      var selector = '[data-value="' + CSS.escape(hash) + '"]';
      var heading = root.querySelector('.tab-content--heading' + selector);
      var body = root.querySelector('.tab-content--body' + selector);

      if (heading || body) {
        activateTab(root, heading, body);
      }

      // Scroll to the very start of the whole "Product description" Shopify section (its
      // outer shopify-section wrapper, above the section's own top padding/spacing), not to
      // the heading/body inside it - scrolling to the heading overshot past the section's own
      // start padding. Falls back to root if the wrapper isn't found for some reason.
      var sectionWrapper = root.closest('.shopify-section.shopify-section-product-description') || root;
      // Wait for the page layout (carousel/slider setup, images, etc.) to actually settle
      // before measuring and scrolling once - not a fixed delay, so this resolves as soon as
      // it's safe (usually well under the cap) instead of always waiting the full amount.
      whenLayoutSettles(function () {
        // A further fixed pause before the very first scroll: the height-settle check above
        // can pass right before one more late reflow (e.g. accordion-component's own init),
        // which was causing a visible "scroll down, drift further, snap back" on first load.
        setTimeout(function () {
          scrollToElement(sectionWrapper);
          correctScrollWhileSettling(sectionWrapper, function () {
            if (heading || body) activateTab(root, heading, body);
          });
        }, 900);
      }, 900);
    } catch (e) {
      if (window.console) console.error('pdp-replacement-tab.js', e);
    }
  }

  function runWithRetries(attemptsLeft) {
    var root = document.querySelector('product-description-component');
    if (root || attemptsLeft <= 0) {
      run();
    } else {
      setTimeout(function () {
        runWithRetries(attemptsLeft - 1);
      }, 200);
    }
  }

  function start() {
    runWithRetries(10);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
